
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ChevronLeft } from 'lucide-react';
import BookingDetails from '@/components/payment/BookingDetails';
import PaymentSummary from '@/components/payment/PaymentSummary';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { BookingInfo } from '@/types/booking';
import { usePaymentSession } from '@/hooks/usePaymentSession';
import { supabase } from '@/integrations/supabase/client';
import TimezoneWarningModal from '@/components/booking/TimezoneWarningModal';
import { getUserTimezone } from '@/utils/timezone-helpers';
import { useSlotReservation } from '@/hooks/useSlotReservation';
import { STANDARD_SESSION_FORMATS } from '@/utils/session-format-helpers';

const Payment = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('booking');
  const [bookingInfo, setBookingInfo] = useState<BookingInfo | null>(null);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [discountCode, setDiscountCode] = useState<string>("");
  const [discount, setDiscount] = useState<number>(0);
  const [couponId, setCouponId] = useState<string | null>(null);
  const { handlePaymentSession, isLoading: paymentLoading } = usePaymentSession();
  const { isReserving, reservationExpiry, reserveSlot, clearReservation } = useSlotReservation();

  // Timezone warning states
  const [showTimezoneWarning, setShowTimezoneWarning] = useState(false);
  const [clientTimezone, setClientTimezone] = useState<string | null>(null);
  const [practitionerTimezone, setPractitionerTimezone] = useState<string>('America/New_York');
  const [pendingPayment, setPendingPayment] = useState(false);
  const [reservedBookingId, setReservedBookingId] = useState<string | null>(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Reserve slot when booking info is loaded
  useEffect(() => {
    const reserveSlotOnLoad = async () => {
      if (!bookingInfo || !bookingInfo.selectedDate || !bookingInfo.selectedTime || reservedBookingId) {
        return;
      }

      console.log("Reserving slot for booking:", bookingInfo);
      const selectedDate = new Date(bookingInfo.selectedDate);

      const bookingId = await reserveSlot(bookingInfo, selectedDate, bookingInfo.selectedTime);

      if (bookingId) {
        setReservedBookingId(bookingId);
        // Store in booking info for payment processing
        setBookingInfo(prev => prev ? { ...prev, bookingId } : prev);
      }
    };

    reserveSlotOnLoad();
  }, [bookingInfo?.practitionerId, bookingInfo?.serviceId, bookingInfo?.selectedDate, bookingInfo?.selectedTime]);

  // Release slot when user leaves the page
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (reservedBookingId && !paymentLoading) {
        // Show warning but don't delete booking
        // The booking will auto-expire after 10 minutes on the server
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [reservedBookingId, paymentLoading]);

  useEffect(() => {
    console.log("Payment page: Loading booking info from sessionStorage");
    const storedBookingInfo = sessionStorage.getItem('bookingInfo');
    console.log("Raw stored booking info:", storedBookingInfo);

    if (storedBookingInfo) {
      try {
        const parsedBookingInfo: BookingInfo = JSON.parse(storedBookingInfo);
        console.log("Parsed booking info:", parsedBookingInfo);
        
        // Validate required fields
        if (!parsedBookingInfo.practitionerId || !parsedBookingInfo.serviceId) {
          throw new Error("Missing required booking IDs");
        }
        
        if (!parsedBookingInfo.selectedDate || !parsedBookingInfo.selectedTime) {
          throw new Error("Missing selected date or time");
        }
        
        setBookingInfo(parsedBookingInfo);
        setIsLoading(false);
      } catch (parseError) {
        console.error("Error parsing booking info from session storage:", parseError);
        setError(t('invalidBookingInfo'));
        setIsLoading(false);
        return;
      }
    } else {
      console.error("No booking information found in sessionStorage");
      
      // Check if there's tempBookingInfo (user came directly to payment without going through datetime)
      const tempBookingInfo = sessionStorage.getItem('tempBookingInfo');
      if (tempBookingInfo) {
        console.log("Found tempBookingInfo, redirecting to datetime selection");
        navigate('/booking-datetime');
        return;
      }
      
      setError(t('noBookingInfo'));
      setIsLoading(false);
    }
  }, [navigate, t]);

  // Load timezone information when booking info is available
  useEffect(() => {
    const loadTimezoneInfo = async () => {
      if (!user || !bookingInfo) return;

      try {
        // Get client timezone
        const userTimezone = await getUserTimezone(user.id);
        setClientTimezone(userTimezone);

        // Get practitioner timezone
        if (bookingInfo.practitionerId) {
          const practitionerTimezone = await getUserTimezone(bookingInfo.practitionerId);
          setPractitionerTimezone(practitionerTimezone);
        }
      } catch (error) {
        console.error('Error loading timezone info:', error);
        // Use America/New_York as fallback
        setClientTimezone('America/New_York');
      }
    };

    loadTimezoneInfo();
  }, [user, bookingInfo]);

// Removed auto-apply coupon for free sessions; coupons must be entered manually

  const handlePaymentSubmit = async () => {
    if (!bookingInfo || !bookingInfo.selectedDate || !bookingInfo.selectedTime) {
      setError(t('missingBookingInfo'));
      return;
    }

    // Check timezone warning conditions
    const isTimezoneNotSet = !clientTimezone;
    const isDifferentTimezone = clientTimezone && clientTimezone !== practitionerTimezone;

    if ((isTimezoneNotSet || isDifferentTimezone) && !pendingPayment) {
      setShowTimezoneWarning(true);
      setPendingPayment(true);
      return;
    }

    console.log("Starting payment with booking info:", bookingInfo);

    // For free bookings ($0), directly mark as paid and navigate to success
    if (bookingInfo.price === 0 && reservedBookingId) {
      try {
        console.log("Free booking detected, marking as paid:", reservedBookingId);

        const { error: updateError } = await supabase
          .from('bookings')
          .update({
            payment_status: 'Paid',
            status: 'Scheduled',
            updated_at: new Date().toISOString()
          })
          .eq('id', reservedBookingId);

        if (updateError) {
          console.error("Error updating free booking:", updateError);
          setError("Failed to confirm booking. Please try again.");
          return;
        }

        // Send confirmation emails
        try {
          await supabase.functions.invoke('send-email', {
            body: {
              template_type: 'bookingConfirmation',
              booking_id: reservedBookingId,
              client_id: user?.id,
              practitioner_id: bookingInfo.practitionerId
            }
          });
        } catch (emailError) {
          console.error("Failed to send confirmation emails:", emailError);
          // Don't fail the booking if email sending fails
        }

        // Navigate to success page
        navigate(`/booking-success?booking_id=${reservedBookingId}`);
        return;
      } catch (error: any) {
        console.error("Free booking error:", error);
        setError(error.message || "Failed to confirm booking. Please try again.");
        return;
      }
    }

    // For paid bookings, proceed with normal payment flow
    try {
      const selectedDate = new Date(bookingInfo.selectedDate);
      await handlePaymentSession(
        bookingInfo,
        selectedDate,
        bookingInfo.selectedTime,
        discount,
        couponId
      );
    } catch (error: any) {
      console.error("Payment error:", error);
      setError(error.message || "Payment failed. Please try again.");
      setPendingPayment(false);
    }
  };

  const handleTimezoneWarningConfirm = () => {
    setShowTimezoneWarning(false);
    // Continue with payment after timezone warning is acknowledged
    handlePaymentSubmitInternal();
  };

  const handlePaymentSubmitInternal = async () => {
    if (!bookingInfo || !bookingInfo.selectedDate || !bookingInfo.selectedTime) {
      setError(t('missingBookingInfo'));
      return;
    }

    console.log("Starting payment with booking info:", bookingInfo);

    try {
      const selectedDate = new Date(bookingInfo.selectedDate);
      await handlePaymentSession(
        bookingInfo,
        selectedDate,
        bookingInfo.selectedTime,
        discount,
        couponId
      );
    } catch (error: any) {
      console.error("Payment error:", error);
      setError(error.message || "Payment failed. Please try again.");
      setPendingPayment(false);
    }
  };

  const handleApplyDiscount = () => {
    // This function will be called by PaymentSummary when a valid coupon is applied
    console.log("Discount applied:", { discountCode, discount, couponId });
  };

  const handleBack = () => {
    try {
      const temp = sessionStorage.getItem('tempBookingInfo');
      if (temp) {
        const parsed = JSON.parse(temp);
        if (parsed.returnToPath) {
          navigate(parsed.returnToPath);
          return;
        }
        if (parsed.practitionerId) {
          navigate(`/book-an-appointment/${parsed.practitionerId}`);
          return;
        }
      }
      if (bookingInfo?.practitionerId) {
        navigate(`/book-an-appointment/${bookingInfo.practitionerId}`);
      } else {
        navigate('/book-an-appointment');
      }
    } catch {
      navigate('/book-an-appointment');
    }
  };
  // Transform BookingInfo to match BookingDetails expected format
  const transformBookingInfo = (info: BookingInfo) => {
    // Parse the actual selected date and time from booking info
    let actualDateTime = new Date();
    
    if (info.selectedDate && info.selectedTime) {
      try {
        const selectedDate = new Date(info.selectedDate);
        const [hours, minutes] = info.selectedTime.split(':').map(Number);
        selectedDate.setHours(hours, minutes, 0, 0);
        actualDateTime = selectedDate;
      } catch (e) {
        console.error("Error parsing date/time:", e);
        // Fallback to tomorrow if parsing fails
        actualDateTime.setHours(actualDateTime.getHours() + 24);
      }
    } else {
      // Fallback to mock date if no actual date/time
      actualDateTime.setHours(actualDateTime.getHours() + 24);
    }
    
    return {
      practitioner: {
        first_name: info.practitionerName.split(' ')[0] || 'Unknown',
        last_name: info.practitionerName.split(' ').slice(1).join(' ') || 'Practitioner',
        professional_title: 'Spiritual Practitioner',
        avatar_url: undefined
      },
      service: {
        service_name: info.service,
        duration_minutes: info.duration,
        price: info.price,
        currency: info.currency
      },
      dateTime: actualDateTime,
      format: (info.format || STANDARD_SESSION_FORMATS.VIRTUAL) as 'virtual' | 'in_person' | 'online',
      location: undefined,
      notes: undefined,
      practitionerTimezone: info.practitionerTimezone || practitionerTimezone || 'America/New_York'
    };
  };

  const isFormComplete = bookingInfo?.selectedDate && bookingInfo?.selectedTime;

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-amber-50/50 py-12 px-4 pt-16">
          <div className="container mx-auto max-w-4xl">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
              <div className="bg-white rounded-lg p-6">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-amber-50/50 py-12 px-4 pt-16">
          <div className="container mx-auto max-w-4xl">
            <Button 
              variant="ghost" 
              onClick={handleBack}
              className="mb-6"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              {t('back')}
            </Button>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!bookingInfo) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-amber-50/50 py-12 px-4 pt-16">
          <div className="container mx-auto max-w-4xl">
            <Alert>
              <AlertDescription>
                {t('noBookingInfo')}
              </AlertDescription>
            </Alert>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow bg-amber-50/50 py-12 px-4 pt-16">
        <div className="container mx-auto max-w-6xl">
          <Button
            variant="ghost"
            onClick={handleBack}
            className="mb-6"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            {t('back')}
          </Button>

          <div className="grid gap-8 lg:grid-cols-2">
            <div className="lg:col-span-1">
              <PaymentSummary
                price={bookingInfo.price}
                currency={bookingInfo.currency}
                discount={discount}
                discountCode={discountCode}
                onDiscountCodeChange={setDiscountCode}
                onApplyDiscount={handleApplyDiscount}
                onPaymentSubmit={handlePaymentSubmit}
                isLoading={paymentLoading || isReserving}
                stripeLoaded={true}
                isUserLoggedIn={!!user}
                isFormComplete={!!isFormComplete}
                onSetCouponId={setCouponId}
                reservationExpiry={reservationExpiry}
              />
            </div>

            <div className="lg:col-span-1">
              <BookingDetails 
                bookingInfo={transformBookingInfo(bookingInfo)}
              />
            </div>
          </div>
        </div>
      </main>
      <Footer />
      
      {/* Timezone Warning Modal */}
      {bookingInfo && (
        <TimezoneWarningModal
          open={showTimezoneWarning}
          onConfirm={handleTimezoneWarningConfirm}
          clientTimezone={clientTimezone}
          practitionerTimezone={practitionerTimezone}
          appointmentTime={new Date(`${bookingInfo.selectedDate} ${bookingInfo.selectedTime}`)}
          practitionerName={bookingInfo.practitionerName}
        />
      )}
    </div>
  );
};

export default Payment;
